import { Box, Button, CircularProgress, Typography } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';

import { isContentLoaded, loadContent, useI18n } from './index';

/**
 * Holds the app back until the readings of the current language are in memory, with a spinner while they
 * download. Switching language from the menu loads first and only then changes language, so this mostly
 * shows on the first visit and when the stored language is not the one that was last fetched.
 */
export const LocaleGate: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const { t, language } = useI18n();
    const [failed, setFailed] = useState(false);
    const [, bump] = useState(0);
    const ready = isContentLoaded(language);

    const load = useCallback(() => {
        setFailed(false);
        loadContent(language)
            .then(() => bump((n) => n + 1))
            .catch(() => setFailed(true));
    }, [language]);

    useEffect(() => {
        if (!ready) load();
    }, [ready, load]);

    if (ready) return <>{children}</>;
    return (
        <Box
            role="status"
            sx={{
                position: 'fixed',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                p: 3,
                textAlign: 'center',
            }}
        >
            {failed ? (
                <>
                    <Typography>{t('app.loadError')}</Typography>
                    <Button variant="outlined" onClick={load}>
                        {t('app.retry')}
                    </Button>
                </>
            ) : (
                <CircularProgress aria-label={t('app.loading')} />
            )}
        </Box>
    );
};
